import React from 'react'
import { useTheme, TextFieldProps, Box, ClickAwayListener, Button } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import ReactSelect, { components, MenuListProps, MultiValueProps, OptionProps, SelectInstance, Theme as SelectTheme } from 'react-select'
import DropdownIndicator from './DropdownIndicator'
import { getSelectStyles } from './styles'
import { getControlItem } from './get-control-item'
import { CheckboxField } from '../InputFields'
import { Typography } from '../Typography'

interface StyleProps {
  isMulti?: boolean
  minimal?: boolean
  isFloating?: boolean
  fullWidth?: boolean
  disabled?: boolean
}

type LabeledOption<T> = {
  label: string | React.ReactElement
  value: T
}

const useSelectStyles = makeStyles<StyleProps>()((theme, props) => ({
  // eslint-disable-next-line tss-unused-classes/unused-classes
  root: {
    minWidth: 150,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  container: {
    minWidth: 150,
    width: props.fullWidth ? '100%' : undefined,
    '& > div': {
      paddingTop: 0,
      height: props.minimal ? 'max-content' : theme.shape.inputField.height,
      maxHeight: props.minimal ? 'max-content' : theme.shape.inputField.height,
      borderRadius: theme.shape.borderRadius,
    },
    p: {
      fontSize: theme.shape.inputField.fontSize,
    },
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  multiValue: {
    padding: 0,
    minWidth: 'fit-content',
    marginLeft: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    maxWidth: 'fit-content',
  },
}))

export interface ShellMultiSelectProps<T> {
  options: LabeledOption<T>[] | undefined
  value?: T[] | undefined
  onChange: (value: T[]) => void
  label?: string
  error?: { message: string } | string
  className?: string
  disabled?: boolean
  clearable?: boolean
  helperText?: string
  noOptionsMessage?: ({ inputValue }: { inputValue: string }) => string | null
  margin?: TextFieldProps['margin']
  fullWidth?: boolean
  placeholder?: string
  dataQa?: string
  required?: boolean
  clearAllLabel?: string
}
/**
 * This component displays a select component with the option to choose multiple items at the same time.
 */
const MultiSelect = <T,>({
  options,
  value,
  onChange,
  label,
  error,
  className,
  disabled,
  clearable = false,
  helperText,
  noOptionsMessage,
  placeholder,
  fullWidth,
  dataQa,
  required,
  clearAllLabel,
}: ShellMultiSelectProps<T>): React.ReactElement => {
  const muiTheme = useTheme()
  const { classes: inputClasses, cx } = useSelectStyles({ isMulti: true })
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const selectRef = React.useRef<SelectInstance<T | null>>(null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Option = (props: OptionProps<any, any>): React.ReactElement => {
    return (
      <components.Option {...props}>
        <CheckboxField sx={{ mx: 2 }} label={props.data.label} checked={props.isSelected} disabled={props.isDisabled} tabIndex={-1} />
      </components.Option>
    )
  }

  const onClearAll = (): void => {
    onChange([])
    setIsMenuOpen(false)
    selectRef.current?.blur()
  }

  const onMenuClose = (): void => {
    setIsMenuOpen(false)
    selectRef.current?.blur()
    selectRef.current?.inputRef?.blur()
    selectRef.current?.setState({ isFocused: false })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const MenuList = (props: MenuListProps<any, true>): React.ReactElement => {
    return (
      <components.MenuList {...props}>
        {props.children}
        {clearAllLabel && (
          <Box sx={{ borderTop: `1px solid ${muiTheme.palette.divider}`, px: 2, py: 1 }}>
            <Button variant="text" color="inherit" onClick={onClearAll} fullWidth>
              {clearAllLabel}
            </Button>
          </Box>
        )}
      </components.MenuList>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const MultiValue = (props: MultiValueProps<any>): React.ReactElement | '' => {
    const valuesLength = props.selectProps.value.length
    const isLast = props.index === valuesLength - 1
    let label = props.data.label

    if (!isLast) {
      label += ', '
    }
    return label
  }

  return (
    <Box>
      <ClickAwayListener onClickAway={onMenuClose}>
        <Box tabIndex={0} onFocus={() => selectRef.current?.focus()}>
          <ReactSelect
            ref={selectRef}
            menuPortalTarget={document.body}
            menuPosition="absolute"
            value={options ? value?.map(item => options.find(option => option.value === item)) : []}
            onChange={items => onChange(items?.map((item: LabeledOption<T>) => item.value) ?? [])}
            noOptionsMessage={noOptionsMessage}
            hideSelectedOptions={false}
            menuIsOpen={isMenuOpen}
            onMenuOpen={() => setIsMenuOpen(true)}
            onMenuClose={onMenuClose}
            onBlur={() => setIsMenuOpen(false)}
            components={{
              DropdownIndicator,
              IndicatorSeparator: () => null,
              Control: getControlItem(label ?? '', !!required, dataQa, !!error, fullWidth, false),
              Option,
              MultiValue,
              MenuList,
            }}
            styles={getSelectStyles(helperText ?? error, muiTheme, false, true, true, { control: { ...muiTheme.typography.body2 } }, true)}
            options={options}
            menuPlacement="auto"
            isClearable={clearable}
            placeholder={placeholder ?? ''}
            isMulti={true}
            required={required}
            isDisabled={disabled}
            className={cx(inputClasses.container, className)}
            theme={(theme: SelectTheme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: muiTheme.palette.primary.main,
              },
            })}
          />
        </Box>
      </ClickAwayListener>
      {helperText && (
        <Typography component={'p'} sx={{ px: '14px', pt: '4px', color: muiTheme.palette.error.main }} variant="caption">
          {helperText}
        </Typography>
      )}
    </Box>
  )
}

export default MultiSelect
